import { useState } from 'react';
import * as React from 'react';

import { translate } from '~l10n/client';
import { atom, useAtom } from 'jotai';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { useLeaveTeam, useTeams } from '~teacher/hook/team';

export const isConfirmLeaveTeamDialogOpenAtom = atom(false);

export function ConfirmLeaveTeamDialog() {
  const [teamId, setTeamId] = useAtom(selectedTeamIdAtom);

  // When you leave the current team, need to make sure the dropdown selects something otherwise
  // the dropdown and page will be empty, leading to confusion.

  const firstTeamId = useTeams({
    select: (teams) => teams.filter((team) => team.id !== teamId)[0]?.id,
  });

  const [isOpen, setIsOpen] = useAtom(isConfirmLeaveTeamDialogOpenAtom);
  const [isLeaving, setIsLeaving] = useState(false);

  const leaveTeamMutation = useLeaveTeam();

  const onClose = () => setIsOpen(false);

  const handleLeaveTeam = () => {
    setIsLeaving(true);
    leaveTeamMutation.mutate(
      { teamId: teamId },
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onSuccess: () => {
          toast.positive('You have successfully left the team');
          onClose();
          setIsLeaving(false);
          setTeamId(firstTeamId.data);

          sendTeacherAmplitudeEvent(EventTypes.TeamsLeaveTeam, {
            team_id: teamId,
          });
        },
        onError: (error) => {
          if (__DEV__) {
            console.error(error);
          }
          toast.critical('Unknown Error. Please try again later.');
          setIsLeaving(false);
        },
      }
    );
  };

  return (
    <DialogConfirmation
      actions={[
        <UIButton
          key="cancel"
          isDisabled={isLeaving}
          aria-disabled={isLeaving}
          onClick={onClose}
        >
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="leave-team"
          appearance="risky"
          isLoading={isLeaving}
          onClick={handleLeaveTeam}
        >
          {translate('Leave Team')}
        </UIButton>,
      ]}
      onClose={onClose}
      id="confirm-leave-team-dialog"
      title={translate('Leave Team')}
      open={isOpen}
    >
      {translate('Are you sure you want to leave this team?')}
    </DialogConfirmation>
  );
}
